import { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/use-auth';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Mail, ExternalLink, Check, AlertCircle, TestTube, Book, Code, FileText } from 'lucide-react';

interface EmailSettings {
  resend_domain: string;
  resend_from_email: string;
  resend_from_name: string;
  app_base_url: string;
}

const NotificationsPage = () => {
  const { hasGlobalRole } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [settings, setSettings] = useState<EmailSettings>({
    resend_domain: '',
    resend_from_email: '',
    resend_from_name: '',
    app_base_url: ''
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const { data, error } = await supabase
        .from('system_settings')
        .select('key, value')
        .in('key', ['resend_domain', 'resend_from_email', 'resend_from_name', 'app_base_url']);

      if (error) throw error;

      const settingsMap = data.reduce((acc, item) => ({
        ...acc,
        [item.key]: item.value
      }), {} as EmailSettings);

      setSettings(settingsMap);
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast({
        title: "Erro",
        description: "Não foi possível carregar as configurações.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const updates = Object.entries(settings).map(([key, value]) => ({
        key,
        value,
        updated_at: new Date().toISOString()
      }));

      for (const update of updates) {
        const { error } = await supabase
          .from('system_settings')
          .update({ value: update.value, updated_at: update.updated_at })
          .eq('key', update.key);

        if (error) throw error;
      }

      toast({
        title: "Sucesso",
        description: "Configurações salvas com sucesso.",
      });
    } catch (error) {
      console.error('Error saving settings:', error);
      toast({
        title: "Erro",
        description: "Não foi possível salvar as configurações.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const testEmailConfiguration = async () => {
    setTesting(true);
    try {
      const { data, error } = await supabase.functions.invoke('send-invitation-email', {
        body: {
          email: 'test@test.com',
          fullName: 'Teste de Configuração',
          companyName: 'Teste de Sistema',
          role: 'company_admin',
          inviterName: 'Administrador',
          invitationId: '00000000-0000-0000-0000-000000000000'
        }
      });

      if (error) {
        console.error('Test error:', error);
        if (error.message?.includes('domain')) {
          toast({
            title: "Erro de Domínio",
            description: "Domínio não verificado no Resend. Verifique se o domínio está configurado e verificado.",
            variant: "destructive",
          });
        } else if (error.message?.includes('API key')) {
          toast({
            title: "Erro de API Key",
            description: "API key do Resend inválida ou não configurada.",
            variant: "destructive",
          });
        } else {
          toast({
            title: "Erro no Teste",
            description: error.message || "Erro desconhecido ao testar configuração.",
            variant: "destructive",
          });
        }
      } else {
        toast({
          title: "✅ Teste Bem-sucedido",
          description: "As configurações de email estão funcionando corretamente!",
        });
      }
    } catch (error: any) {
      console.error('Test error:', error);
      toast({
        title: "Erro no Teste",
        description: error.message || "Erro desconhecido ao executar o teste.",
        variant: "destructive",
      });
    } finally {
      setTesting(false);
    }
  };

  if (!hasGlobalRole('portal_admin')) {
    return (
      <div className="text-center py-8">
        <h1 className="text-2xl font-bold mb-4">Acesso negado</h1>
        <p className="text-muted-foreground">
          Você não tem permissão para acessar as configurações de notificações.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  const isDomainConfigured = settings.resend_domain && settings.resend_domain.length > 0;
  const isEmailValid = settings.resend_from_email.includes('@') && 
    settings.resend_from_email.includes(settings.resend_domain);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Notificações</h1>
        <p className="text-muted-foreground">
          Configure os serviços de envio de notificações por email.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Configurações de Email (Resend)
          </CardTitle>
          <CardDescription>
            Configure o domínio e emails para envio de convites e notificações.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="baseUrl">URL Base da Aplicação</Label>
            <Input
              id="baseUrl"
              type="url"
              placeholder="https://www.portalone.adm.br"
              value={settings.app_base_url}
              onChange={(e) => setSettings({ ...settings, app_base_url: e.target.value })}
            />
            <p className="text-sm text-muted-foreground">
              URL utilizada nos links dos emails enviados pela plataforma.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="domain">Domínio Verificado</Label>
            <div className="flex gap-2">
              <Input
                id="domain"
                placeholder="portalone.adm.br"
                value={settings.resend_domain}
                onChange={(e) => setSettings({ ...settings, resend_domain: e.target.value })}
              />
              {isDomainConfigured && (
                <Badge variant="outline" className="flex items-center gap-1">
                  <Check className="h-3 w-3" />
                  Configurado
                </Badge>
              )}
            </div>
            <p className="text-sm text-muted-foreground">
              Domínio verificado no Resend para envio de emails.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="fromEmail">Email Remetente</Label>
            <Input
              id="fromEmail"
              type="email"
              placeholder="noreply@portalone.adm.br"
              value={settings.resend_from_email}
              onChange={(e) => setSettings({ ...settings, resend_from_email: e.target.value })}
            />
            <p className="text-sm text-muted-foreground">
              Email que aparecerá como remetente nas mensagens.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="fromName">Nome do Remetente</Label>
            <Input
              id="fromName"
              placeholder="PortalONE Suite"
              value={settings.resend_from_name}
              onChange={(e) => setSettings({ ...settings, resend_from_name: e.target.value })}
            />
            <p className="text-sm text-muted-foreground">
              Nome que aparecerá como remetente nas mensagens.
            </p>
          </div>

          {!isEmailValid && settings.resend_from_email && settings.resend_domain && (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                O email deve usar o domínio configurado ({settings.resend_domain}).
              </AlertDescription>
            </Alert>
          )}

          <div className="flex flex-col sm:flex-row gap-2">
            <Button 
              onClick={saveSettings} 
              disabled={saving}
              className="flex-1"
            >
              {saving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Salvando...
                </>
              ) : (
                "Salvar Configurações"
              )}
            </Button>
            
            <Button 
              variant="outline" 
              onClick={testEmailConfiguration}
              disabled={testing || !isEmailValid}
              className="flex-1"
            >
              {testing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Testando...
                </>
              ) : (
                <>
                  <TestTube className="mr-2 h-4 w-4" />
                  Testar Configuração
                </>
              )}
            </Button>
          </div>

          <div className="border-t pt-6">
            <h3 className="text-lg font-medium mb-4">Documentação e Recursos</h3>
            <div className="grid gap-4 md:grid-cols-3">
              <Button variant="outline" asChild>
                <a href="https://resend.com/docs" target="_blank" rel="noopener noreferrer">
                  <Book className="mr-2 h-4 w-4" />
                  Documentação
                  <ExternalLink className="ml-2 h-3 w-3" />
                </a>
              </Button>
              
              <Button variant="outline" asChild>
                <a href="https://resend.com/api-keys" target="_blank" rel="noopener noreferrer">
                  <Code className="mr-2 h-4 w-4" />
                  API Keys
                  <ExternalLink className="ml-2 h-3 w-3" />
                </a>
              </Button>
              
              <Button variant="outline" asChild>
                <a href="https://resend.com/domains" target="_blank" rel="noopener noreferrer">
                  <FileText className="mr-2 h-4 w-4" />
                  Verificar Domínio
                  <ExternalLink className="ml-2 h-3 w-3" />
                </a>
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default NotificationsPage;